import React, { useEffect, useState } from "react";
import { FormattedMessage,  useIntl } from "react-intl";

import { generalGenres, planTypes, tempos, clefs, keys, scales, rhytPatterns, i18nSolfa, lengthenSolfa } from "../../../common/parameters";
import { get } from "../../actions/general";

import { localizeInterval } from "../../../common/parameters";
import Seo from "../../../common/helpers/seo";

import { Card, StyledABCname } from "../../pages/plan/Meta/styles";

import {
    Checkbox,
    Select,
    Label,
    InputSelectCard,
    Radio,
    FormElement,
    InputNumber,
    TwoInOne,
} from "../../components/Form";
import ComposerCard from "../../components/ComposerCard";
import GenreCard from "../../components/GenreCard";

import { StyledArticle } from "../styles";
import { P1, P2, H2, H4 } from "../styles";

import { StyledSpring, Flex, Column, Desktop, Mobile } from "./styles";
import messages from "./messages";
import { useDispatch } from "react-redux";
import { IComposerData } from "../../pages/browse/Opus/interfaces";
import { IComposerResponse, IGenre } from "../../pages/plan/Meta/Opus/interfaces";
import { AxiosResponse } from "axios";
import { formatComposerName } from "../../../common/helpers/formatComposerName";
import { useUser } from "../../store/user/selectors";
import FormattedHtmlMessage from "../../../common/helpers/FormattedHtmlMessage";

const General = () => {
    const { formatMessage } = useIntl();
    return (
        <div>
            <H4>
                <FormattedMessage {...messages.articleOpusTitle} />
            </H4>
            <P2>
                <FormattedMessage {...messages.articleOpusDescription} />
            </P2>
            <div>
                <Radio label={formatMessage(messages.type)} value={"written"} options={planTypes(formatMessage)} onChange={() => { }} />
                <Checkbox label={formatMessage(messages.isLyrics)} checked={true} onChange={() => { }} />
                <Checkbox label={formatMessage(messages.isCanon)} checked={true} onChange={() => { }} />
            </div>
        </div>
    );
}

const ComposerAndGenre = () => {
    const dispatch = useDispatch();
    const { formatMessage, locale } = useIntl();
    const [composers, setComposers] = useState<IComposerData[]>([]);
    const [genres, setGenres] = useState<IGenre[]>([]);
    useEffect(() => {
        dispatch(get("composer-list", (res: AxiosResponse<{ composers: IComposerResponse[] }>) => {
            const rawData = res.data.composers;
            const composersData: IComposerData[] = [];
            rawData.forEach(data => {
                composersData.push({
                    label: formatComposerName(data.name, locale),
                    id: data.id,
                    era: data.era,
                    card: (
                        <ComposerCard
                            img={data.img}
                            name={formatComposerName(data.short!, locale)}
                            birth={data.birth}
                            death={data.death}
                            period={data.era}
                            country={data.country}
                            small
                        />
                    ),
                });
            });
            setComposers(composersData);
        }));

        const genresData = generalGenres(formatMessage).map(genre => {
            return {
                label: genre.label,
                id: genre.value,
                card: <GenreCard img={genre.img} name={genre.label} details={genre.details} small />,
            };
        });
        setGenres(genresData);
    }, []);

    return (
        <div>
            <InputSelectCard
                name="composer"
                label={formatMessage(messages.composer)}
                value={""}
                onChange={() => { }}
                options={composers}
                save={() => { }}
                createNew={{
                    label: <FormattedMessage {...messages.addNewComposer} />,
                    onClick: () => { },
                }}
                simulation
                simulationInput={"b"}
            />
            <InputSelectCard
                name="genre"
                label={formatMessage(messages.genre)}
                value={""}
                onChange={() => null}
                options={genres}
                save={() => null}
                simulation
                simulationInput={"e"}
            />
        </div>
    );
}

const Pitch = () => {
    const { formatMessage, locale } = useIntl();
    const { solfaShort = false, solfaKam = false} = useUser();
    return (
        <div>
            <H4>
                <FormattedMessage {...messages.articlePitchTitle} />
            </H4>
            <P2>
                <FormattedMessage {...messages.articlePitchDescription} />
            </P2>
            <div>
                <Select name="clef" label={formatMessage(messages.clef)} value={"treble"} onChange={() => { }} options={clefs(formatMessage)} />
                <Select name="key" label={formatMessage(messages.key)} value={"0"} onChange={() => { }} options={keys} />
                <FormElement>
                    <Label>
                        <FormattedMessage {...messages.solfaNames} />
                    </Label>
                    <div>
                        {["d", "r", "m", "s", "l"].map(solfaName => {
                            return <Card key={solfaName}>{lengthenSolfa(i18nSolfa(solfaName, locale, solfaKam), locale, solfaShort)}</Card>;
                        })}
                    </div>
                    <div>
                        {["c’", "d’", "e’", "g’", "a’"].map(ABCname => {
                            return <StyledABCname key={ABCname}>{ABCname}</StyledABCname>;
                        })}
                    </div>
                </FormElement>
            </div>
            <div>
                <Select name="scale" label={formatMessage(messages.scale)} value={"pentaton"} onChange={() => { }} options={scales(formatMessage)} />
                <FormElement>
                    <Label>
                        <FormattedMessage {...messages.range} />
                    </Label>
                    <div>
                        <span>
                            <Card>{lengthenSolfa(i18nSolfa("d", locale, solfaKam), locale, solfaShort)}</Card> - <Card>{lengthenSolfa(i18nSolfa("l", locale, solfaKam), locale, solfaShort)}</Card> | {localizeInterval("N6", locale)}
                        </span>
                    </div>
                    <div>
                        <span>
                            <StyledABCname>c'</StyledABCname> - <StyledABCname>a'</StyledABCname>
                        </span>
                    </div>
                </FormElement>
                <FormElement>
                    <Label>
                        <FormattedMessage {...messages.intervals} />
                    </Label>
                    <div>
                        {["N2", "k3", "N3", "T4"]
                            .map(interval => {
                                return localizeInterval(interval, locale);
                            })
                            .map(interval => {
                                return <Card key={interval}>{interval}</Card>;
                            })}
                    </div>
                </FormElement>
            </div>
        </div>
    );
}

const Rhythm = () => {
    const { formatMessage } = useIntl();
    return (
        <div>
            <H4>
                <FormattedMessage {...messages.articleRhythmTitle} />
            </H4>
            <P2>
                <FormattedMessage {...messages.articleRhythmDescription} />
            </P2>
            <div>
                <Select name="tempoFeel" label={formatMessage(messages.tempoFeel)} value={"slow"} onChange={() => { }} options={tempos(formatMessage)} />
                <div>
                    <Label>
                        <FormattedMessage {...messages.timeSignature} />
                    </Label>
                    <TwoInOne style={{ maxWidth: 133 }}>
                        <InputNumber name="tsnom" value="3" onChange={() => { }} />
                        <div className={"Gap"}>/</div>
                        <InputNumber name="tsden" value="4" onChange={() => { }} />
                    </TwoInOne>
                </div>
                <Checkbox label={formatMessage(messages.isUpbeat)} checked={true} onChange={() => { }} />
            </div>
            <div>
                <FormElement>
                    <Label>
                        <FormattedMessage {...messages.rhythmPatterns} />
                    </Label>
                    <div>
                        {["titi", "ta-a", "tiriri", "synkope"].map(pattern => {
                            return <Card key={pattern}>{rhytPatterns(formatMessage)[pattern]}</Card>;
                        })}
                    </div>
                </FormElement>
            </div>
        </div>
    );
};

const RhytNameArticle = () => {
    const { formatMessage } = useIntl();
    return (
        <StyledSpring withShadow marginTop={50}>
            <StyledArticle>
                <Seo
                    title={formatMessage(messages.articleTitle)}
                    type={"article"}
                    description={formatMessage(messages.articleDescription)}
                />
                <H2>
                    <FormattedMessage {...messages.articleTitle} />
                </H2>
                <P1 center>
                    <FormattedHtmlMessage {...messages.articleDescription} />
                </P1>
                <Flex>
                    <Desktop>
                        <Column>
                            <General />
                            <Pitch />
                        </Column>
                        <Column>
                            <ComposerAndGenre />
                            <Rhythm />
                        </Column>
                    </Desktop>
                    <Mobile>
                        <General />
                        <ComposerAndGenre />
                        <Pitch />
                        <Rhythm />
                    </Mobile>
                </Flex>
            </StyledArticle>
        </StyledSpring>
    );
}

export default RhytNameArticle;
